import React, { useCallback, useEffect, useState } from 'react';

import { faFolderMinus, faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { Badge } from '@buildoutinc/blueprint-react/ui/Badge';
import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import { Input } from '@buildoutinc/blueprint-react/ui/Input';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';
import { Table } from '@buildoutinc/blueprint-react/ui/Table';

import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import LoadingSpinner from 'components/ui/loading_spinner';
import { Select } from 'components/ui/select';
import { showSnackbar } from 'components/ui/snackbar/helper';
import Dropdown from 'components/dropdown';
import { deleteRequest, getRaw } from 'helpers/api';
import { formatDate } from 'helpers/formatters/date_time_formats';

import OrientationBadge from '../orientation_badge';
import { Orientation } from '../types';
import { marketOverviewsUrls, type CollectionPage } from './types';

interface Props {
  companyId: string | null;
  libraryBase: string;
}

interface OrientationOption {
  value: Orientation;
  label: string;
}

const ORIENTATION_OPTIONS: OrientationOption[] = [
  { value: Orientation.Portrait, label: 'Portrait' },
  { value: Orientation.Landscape, label: 'Landscape' },
  { value: Orientation.Square, label: 'Square' },
  { value: Orientation.Mixed, label: 'Mixed' },
];

const matchesSearch = (page: CollectionPage, search: string) => {
  const term = search.trim().toLowerCase();
  if (term === '') return true;

  return [page.name, page.market_name ?? ''].some(text => text.toLowerCase().includes(term));
};

export default function CollectionPages({ companyId, libraryBase }: Props) {
  const [pages, setPages] = useState<CollectionPage[] | null>(null);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [search, setSearch] = useState('');
  const [orientation, setOrientation] = useState<OrientationOption | null>(null);
  const [removing, setRemoving] = useState<CollectionPage | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const urls = marketOverviewsUrls(libraryBase);
  const companyQuery = companyId ? `?company_id=${companyId}` : '';
  const detailUrl = (page: CollectionPage) => `${libraryBase}/pages/pdf/${page.id}${companyQuery}`;

  const fetchPages = useCallback(async () => {
    try {
      const response: { pages: CollectionPage[] } = await getRaw(`${urls.collectionPages}${companyQuery}`);
      setPages(response.pages);
      setHasLoadError(false);
    } catch {
      setHasLoadError(true);
    }
  }, [urls.collectionPages, companyQuery]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const removePage = async () => {
    if (!removing) return;

    setIsSaving(true);
    try {
      await deleteRequest(urls.collectionPage(removing.id), { params: { companyId: companyId || undefined } });
      showSnackbar({ text: `Removed ${removing.name} from Market Overviews.`, variant: 'success' });
      setRemoving(null);
      await fetchPages();
    } catch {
      showSnackbar({ text: "The page couldn't be removed. Try again.", variant: 'danger' });
    } finally {
      setIsSaving(false);
    }
  };

  const renderPages = () => {
    if (hasLoadError) {
      return (
        <Alert severity="destructive">
          <Alert.Title>The market pages could not be loaded.</Alert.Title>
          <Button variant="secondary" onClick={fetchPages}>Try again</Button>
        </Alert>
      );
    }

    if (!pages) return <LoadingSpinner size="md" skipTopBuffer />;

    if (pages.length === 0) {
      return (
        <Empty>
          <Empty.Title>No market pages yet</Empty.Title>
          <Empty.Content>
            Move a PDF page here from the Page Library with &quot;Move to Market Overviews&quot;, then tag it with a
            market.
          </Empty.Content>
        </Empty>
      );
    }

    const shown = pages.filter(page => (
      matchesSearch(page, search) && (!orientation || page.orientation === orientation.value)
    ));

    if (shown.length === 0) {
      return (
        <Empty>
          <Empty.Title>No market pages match</Empty.Title>
          <Empty.Content>Try a different page name, market or orientation.</Empty.Content>
        </Empty>
      );
    }

    return (
      <Table className="align-middle table-hover">
        <Table.Header>
          <Table.Row>
            <Table.Head>Name</Table.Head>
            <Table.Head>Market</Table.Head>
            <Table.Head>Orientation</Table.Head>
            <Table.Head>Last Edited</Table.Head>
            <Table.Head />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {shown.map(page => (
            <Table.Row key={page.id}>
              <Table.Cell>
                <a href={detailUrl(page)}>{page.name}</a>
              </Table.Cell>
              <Table.Cell>
                {page.market_name ?? <Badge variant="outline">No market, not used</Badge>}
              </Table.Cell>
              <Table.Cell>
                <OrientationBadge orientation={page.orientation} />
              </Table.Cell>
              <Table.Cell>{page.updated_at ? formatDate(page.updated_at) : '---'}</Table.Cell>
              <Dropdown.Cell style={{ height: 'auto' }}>
                <Dropdown.Item
                  icon={faPenToSquare}
                  label="Edit page"
                  onClick={() => {
                    window.location.href = detailUrl(page);
                  }}
                />
                <Dropdown.Item
                  disabled={!page.owned}
                  disabledMessage="Your parent company manages this page"
                  icon={faFolderMinus}
                  label="Remove from Market Overviews"
                  onClick={() => setRemoving(page)}
                />
              </Dropdown.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    );
  };

  return (
    <Card className="mb-4">
      <Card.Body>
        <div className="d-flex flex-wrap gap-2 mb-3">
          <div className="flex-grow-1">
            <Input
              aria-label="Search by page name or market"
              placeholder="Search by page name or market"
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            />
          </div>
          <div style={{ minWidth: '200px' }}>
            <Select<OrientationOption>
              aria-label="Orientation"
              isClearable
              options={ORIENTATION_OPTIONS}
              placeholder="All orientations"
              value={orientation}
              onChange={(option: OrientationOption | null) => setOrientation(option)}
            />
          </div>
        </div>
        {renderPages()}
      </Card.Body>

      <Modal open={removing !== null} onClose={() => !isSaving && setRemoving(null)}>
        <Modal.Content centered>
          <Modal.Header>
            <Modal.Title>Remove this page from Market Overviews?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {removing?.name} stops filling the Market Overview slot. It stays in your Page Library, in no documents
            until you add it to some.
          </Modal.Body>
          <Modal.Footer>
            <Button disabled={isSaving} variant="ghost" onClick={() => setRemoving(null)}>Cancel</Button>
            <Button disabled={isSaving} variant="primary" onClick={removePage}>Remove from Market Overviews</Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    </Card>
  );
}
